<template>
  <div
    :class="['card', themeClass, classes]"
    :style="{ backgroundColor: backgroundColor }"
  >
    <div
      v-if="!!backgroundImage"
      aria-label="ASAA"
      class="card-image"
      role="img"
      :alt="backgroundAltText"
      :style="{ backgroundImage: `url(${backgroundImage})` }"
    />
    <div
      v-if="overlay && !!backgroundImage"
      class="card-overlay"
    >
      <div
        v-if="!!iconImage"
        class="card-icon"
        role="img"
        :alt="iconAltText"
        :style="{ backgroundImage: `url(${iconImage})` }"
      />
      <slot />
    </div>
    <div
      v-else
      class="card-body"
    >
      <slot name="header" />
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * `Cards` are generally used to contain content that should be visually grouped together on the page.
 * They can be used in sliders or on their own as floating elements on the page.
 */
export default {
  name: 'Card',
  props: {
    /**
     * Sets the alt text on the background image.
     */
    backgroundAltText: {
      type: String,
      default: null
    },
    /**
     * Sets the background color of the lower portion of the card.
     */
    backgroundColor: {
      type: String,
      default: null
    },
    /**
     * The source of the card's background image, if there is one.
     * Appends `-image` to card class for styling purposes if not null.
     */
    backgroundImage: {
      type: String,
      default: null
    },
    /**
    * Adjusts CSS for use within a slider, row or other flex-container where multiple cards are evenly spaced out.
    */
    contained: {
      type: Boolean,
      default: false
    },
    /**
     * Use dark theme for component.
     */
    dark: {
      type: Boolean,
      default: false
    },
    /**
     * Designed for use in sliders, carousels, etc.
     * When used in conjunction with "contained" parameter, removes max width limits on card.
     */
    fillWidth: {
      type: Boolean,
      default: false
    },
    /**
     * The card's `flex-direction`.
     * @values column, row
     */
    flexDirection: {
      type: String,
      default: 'column',
      validator: value => {
        return value.match(/(column|row)/)
      }
    },
    /**
     * Sets the alt text on the icon image.
     */
    iconAltText: {
      type: String,
      default: null
    },
    /**
     * The card's icon image. Only shows if `overlay` is set to true.
     */
    iconImage: {
      type: String,
      default: null
    },
    /**
     * Whether the card should be built as an overlay above an image with a solid background.
     * Requires `backgroundImage` to be set.
     */
    overlay: {
      type: Boolean,
      default: false
    },
    /**
     * Card column width.
     * @values single, wide, full
     */
    width: {
      type: String,
      default: 'single',
      validator: value => {
        return value.match(/(single|wide|full)/)
      }
    }
  },
  computed: {
    classes() {
      // assign class based on flexDirection, size, and background image
      return {
        [`card--flex-${this.flexDirection}`]: true,
        [`card--${this.width}${this.backgroundImage ? '-image' : ''}`]: true,
        'card--overlay': this.overlay,
        'card--container-mode': this.contained,
        'card--fill-width': this.fillWidth
      }
    },
    themeClass() {
      return {
        'theme-light': !this.dark,
        'theme-dark': this.dark
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.card {
  @include reset;
  @include lhCrop(1.2);
  // apply theme styles
  @include themify($themes) {
    background-color: themed('background');
    color: themed('text');
  }
  display: flex;
  align-items: center;
  text-align: center;
  width: 100%;
  margin: $space-xs;
  &.card--container-mode {
    // remove default left and right margins when contained
    margin: $space-xs 0;
  }
  &.card--flex-row {
    flex-direction: row;
  }
  &.card--flex-column {
    flex-direction: column;
  }
  /**
  * DEFAULT FONT STYLING
  */
  h2 {
    @include typographylargeheadingh2;
  }
  h3 {
    @include typographylargeheadingh3;
    margin: $space-m $space-s;
  }
  h4 {
    @include typographylargeheadingh4;
    margin-top: $space-xs;
  }
  h5 {
    @include typographylargeheadingh5;
    margin-top: $space-xs;
  }
  h6 {
    @include typographylargeheadingh6;
    margin-top: $space-xs;
  }
  p {
    @include typographylargeparagraphsmallcopy_regular;
    margin: $space-m;
  }
  @include media('phone') {
    h2 {
      @include typographysmallheadingh2;
      margin-top: $space-xs;
      margin-bottom: $space-s;
    }
    h3 {
      @include typographysmallheadingh3;
      margin: $space-xs 0;
    }
    h4 {
      @include typographysmallheadingh4;
      margin-top: $space-xs;
    }
    h5 {
      @include typographysmallheadingh5;
    }
    h6 {
      @include typographysmallheadingh6;
    }
    p {
      @include typographysmallparagraphsmallcopy_regular;
      margin: $space-m;
    }
  }
  &.card--overlay {
    h3 {
      margin-top: $space-m;
    }
    @include media('phone') {
      h3 {
        margin-bottom: $space-s;
      }
      p {
        margin: $space-m $space-s
      }
    }
  }
  /**
  * CARD SUB-COMPONENT STYLING
  */
  .card-image {
    width: 100%;
    background-repeat: no-repeat;
    background-size: cover;
    object-fit: cover;
    min-height: 184px;
    @include media('phone') {
      min-height: 0;
      height: 160px;
    }
  }
  .card-overlay {
    background-color: $white;
    height: calc(100% - #{$space-s});
    width: calc(100% - 2 * #{$space-m + $space-s});
    margin-top: -(2*$space-xl);
    display: block;
    padding: $space-m $space-s;
    margin-bottom: $space-m;
    text-align: center;
    p {
      margin: $space-s $space-xxs;
    }
  }
  .card-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-repeat: no-repeat;
    background-size: cover;
    margin: calc(-2 * #{$space-xl}) auto 0;
  }
  .card-body {
    padding: $space-m $space-s;
  }
  /**
  * CARD WIDTH STYLING
  */
  &.card--single,
  &.card--single-image {
    max-width: 348px;
    @include media('phone') {
      max-width: 328px;
    }
  }
  // Single width card stylings
  &.card--single {
    width: calc(33.33% - 64px);
    max-width: 328px;
    padding: $space-m;
    &.card--container-mode {
      width: 100%;
      &.card--fill-width {
        max-width: none;
      }
    }

    @include media('phone') {
      width: calc(100% - #{$space-s});
      margin: 0;
      padding: $space-s;
    }
  }
  &.card--single-image {
    width: calc(33.33% - 16px);
    &.card--container-mode {
      width: 100%;
      &.card--fill-width {
        max-width: none;
      }
    }
    @include media('phone') {
      width: 100%;
      margin: $space-s 0;
    }
  }
  &.card--wide,
  &.card--wide-image {
    max-width: 696px;
    @include media('phone') {
      max-width: 360px;
    }
  }
  // Extra width card stylings
  &.card--wide {
    width: calc(66.33% - 64px);
    padding: $space-m;
    &.card--container-mode {
      width: 100%;
      &.card--fill-width {
        max-width: none;
      }
    }
    @include media('phone') {
      width: calc(100% - #{$space-s});
      margin: $space-s;
    }
  }
  &.card--wide-image {
    width: calc(66.33% - #{$space-m});
    &.card--container-mode {
      width: 100%;
      &.card--fill-width {
        max-width: none;
      }
    }
    @include media('phone') {
      width: 100%;
      margin: $space-s 0;
    }
  }
  // Full width card stylings
  &.card--full {
    padding: $space-m;
  }
  &.card--full,
  &.card--full-image {
    max-width: 1084px;
    .card-image {
        height: 256px;
    }
    @include media('phone') {
      max-width: 360px;
      .card-image {
        height: 160px;
      }
    }
    &.card--overlay {
      .card-overlay {
        width: 81%;
        margin-top: -(4*$space-xl);
        padding: $space-m $space-s;
        margin-bottom: ($space-xl + $space-m);
        @include media('desktop') {
          h2 {
            margin: $space-l (2 * $space-xxl);
          }
          h6 {
            margin: $space-s (3 * $space-xxl + $space-xs);
          }
          p {
            @include typographysmallparagraphbodycopy_regular;
            margin-left: 3 * $space-xxl;
            margin-right: 3 * $space-xxl;
          }
        }
        @include media('phone') {
          margin-top: -(2*$space-xl);
          max-width: 328px;
          width: calc(100% - 2 * #{$space-xxl});
          margin-bottom: $space-s;
          p {
            margin-top: $space-xs;
            margin-bottom: 0;
          }
        }
      }
    }
  }
  /**
  * SMALL OVERLAY CARD STYLING
  */
  @include media('phone') {
    &.card--overlay {
      h3 {
        margin-top: $space-m;
      }
      p {
        margin: $space-m $space-xs;
      }
      .card-overlay {
        margin-top: -(2*$space-m);
        p {
          margin-bottom: $space-s;
        }
      }
    }
  }
}
</style>
